import { useMutation, useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query';

import { ATTENDANCE_QUERY_KEYS } from './keys';

import * as attendanceService from 'services/attendance';
import * as worklogService from 'services/worklog';

import { handleError } from 'utils/errorHandler';

import type { WorklogSaveBody } from 'interface/leave';
import type { ServiceId, ServiceParams } from 'interface/service';

export const getAttendanceCalendarQueryKey = (filter: ServiceParams = {}) =>
  [ATTENDANCE_QUERY_KEYS.CALENDAR, filter] as const;

export const attendanceCalendarQueryOptions = (filter: ServiceParams = {}) => ({
  queryKey: getAttendanceCalendarQueryKey(filter),
  queryFn: () => attendanceService.fetch(filter),
});

export const useFetchAttendanceCalendarQuery = <TData = unknown>(
  filter: ServiceParams = {},
  enabled = true
): UseQueryResult<TData, Error> => {
  return useQuery({
    ...attendanceCalendarQueryOptions(filter),
    enabled,
  }) as UseQueryResult<TData, Error>;
};

export const getWorkStatusSummaryQueryKey = (filter: ServiceParams = {}) =>
  [ATTENDANCE_QUERY_KEYS.WORK_STATUS_SUMMARY, filter] as const;

export const useFetchWorkStatusSummaryQuery = <TData = unknown>(
  filter: ServiceParams = {},
  enabled = true
): UseQueryResult<TData, Error> => {
  return useQuery({
    queryKey: getWorkStatusSummaryQueryKey(filter),
    queryFn: () => attendanceService.fetchWorkStatusSummary(filter) as Promise<TData>,
    enabled,
  });
};

export const getUnfreezeRequestsQueryKey = (userId?: ServiceId) =>
  [ATTENDANCE_QUERY_KEYS.UNFREEZE_REQUESTS, userId] as const;

export const useFetchUnfreezeRequestsQuery = <TData = unknown>(
  userId?: ServiceId,
  enabled = true
): UseQueryResult<TData, Error> => {
  return useQuery({
    queryKey: getUnfreezeRequestsQueryKey(userId),
    queryFn: () => attendanceService.fetchUnfreezeRequestsByUserId(userId!) as Promise<TData>,
    enabled: Boolean(userId) && enabled,
  });
};

export const useSaveWorklogMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (worklog: WorklogSaveBody) => worklogService.save(worklog),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [ATTENDANCE_QUERY_KEYS.CALENDAR],
      });
      queryClient.invalidateQueries({
        queryKey: [ATTENDANCE_QUERY_KEYS.SUMMARY],
      });
      queryClient.invalidateQueries({
        queryKey: [ATTENDANCE_QUERY_KEYS.WORK_STATUS_SUMMARY],
      });
    },
    onError: (error) => handleError(error),
  });
};
